import { graphql } from 'react-relay';

import type { RefetchSupportedType } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { expValEquals } from '@confluence/feature-experiments';
import type { NetworkStatus } from '@confluence/apollo-relay-interop';
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';

import type {
	usePermissionCheckPermissionCheckContentQuery as usePermissionCheckPermissionCheckContentQuery$data,
	usePermissionCheckPermissionCheckContentQueryVariables as usePermissionCheckPermissionCheckContentQuery$variables,
} from './__types__/usePermissionCheckPermissionCheckContentQuery.ts';
export const usePermissionCheck = (
	contentId: string,
): {
	hasData: boolean;
	canEdit: boolean;
	loading: boolean;
	contentQueryError: ApolloErrorInterop | undefined;
	refetch: RefetchSupportedType<
		usePermissionCheckPermissionCheckContentQuery$data,
		usePermissionCheckPermissionCheckContentQuery$variables
	>;
	networkStatus: NetworkStatus;
} => {
	const { data, loading, error, refetch, networkStatus } = useQueryInterop<
		usePermissionCheckPermissionCheckContentQuery$data,
		usePermissionCheckPermissionCheckContentQuery$variables
	>(
		graphql`
			query usePermissionCheckPermissionCheckContentQuery($contentId: ID!, $status: [String]) {
				content(id: $contentId, status: $status) {
					nodes {
						...ContentOperationsFragment @relay(mask: false)
					}
				}
			}
		`,
		expValEquals('confluence_content_operations_cache_first', 'cohort', 'experiment')
			? {
					variables: {
						contentId,
						status: ['current', 'archived', 'draft'],
					},
					notifyOnNetworkStatusChange: true,
					fetchPolicy: 'cache-and-network' as const,
				}
			: {
					variables: {
						contentId,
					},
					notifyOnNetworkStatusChange: true,
					fetchPolicy: 'cache-and-network' as const,
				},
	);

	const contentNode = data?.content?.nodes?.[0];
	const operations = contentNode?.operations ?? [];
	const contentType = contentNode?.type ?? undefined;

	const editPermission = operations.some(
		(operationResults) =>
			operationResults?.operation === 'update' && operationResults?.targetType === contentType,
	);

	return {
		hasData: Boolean(data?.content?.nodes),
		canEdit: editPermission,
		loading,
		contentQueryError: error,
		refetch,
		networkStatus,
	};
};
